/**
 * Image Caching Utility
 * 
 * Uses a Cloud Function to download external images and upload them to Firebase Storage.
 * This bypasses CORS and HTTP restrictions that would fail on iOS clients.
 */

import { secureImageUrl } from './secureImageUrl';

/**
 * Downloads an image from a URL and uploads it to Firebase Storage via Cloud Function
 * 
 * @param {string} imageUrl - The original image URL
 * @param {string} productId - Unique identifier for the product (used in storage path)
 * @returns {Promise<{url: string, cached: boolean, originalUrl: string}>} - Result object with caching status
 */
export async function cacheImageToFirebase(imageUrl, productId) {
  // Skip if already a Firebase Storage URL
  if (imageUrl.includes('firebasestorage.googleapis.com')) {
    console.log('Image already cached in Firebase Storage');
    return { url: imageUrl, cached: true, originalUrl: imageUrl };
  }

  // Skip data URIs (base64 images)
  if (imageUrl.startsWith('data:')) {
    console.log('Data URI detected, skipping cache');
    return { url: imageUrl, cached: true, originalUrl: imageUrl };
  }

  try {
    console.log('🔄 Caching image to Firebase Storage via Cloud Function...', imageUrl.substring(0, 60));

    // Import Firebase Functions dynamically
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const functions = getFunctions();
    const downloadAndCache = httpsCallable(functions, 'downloadAndCacheImage');

    // Call the Cloud Function to download and cache the image
    const result = await downloadAndCache({
      imageUrl,
      productId
    });

    if (result.data.success && result.data.url) {
      console.log('✅ Image cached successfully:', result.data.message);
      return {
        url: result.data.url,
        cached: true,
        originalUrl: imageUrl
      };
    } else if (result.data.cachingFailed) {
      console.warn('⚠️ Caching failed:', result.data.message);
      // For HTTP-only images that can't be cached, keep original URL for web compatibility
      const fallbackUrl = result.data.url || imageUrl;
      return {
        url: fallbackUrl,
        cached: false,
        originalUrl: imageUrl,
        error: result.data.error
      };
    } else {
      console.warn('⚠️ Cloud Function returned unexpected response:', result.data);
      return {
        url: secureImageUrl(imageUrl),
        cached: false,
        originalUrl: imageUrl
      };
    }

  } catch (error) {
    console.error('⚠️ Failed to cache image via Cloud Function:', error.message);
    console.log('📌 Using HTTPS fallback URL');
    
    // Fallback to HTTPS version of the original URL
    return {
      url: secureImageUrl(imageUrl),
      cached: false,
      originalUrl: imageUrl,
      error: error.message
    };
  }
}

/**
 * Batch cache multiple images
 * 
 * @param {Array<{url: string, productId: string}>} images - Array of image objects
 * @returns {Promise<Array<string>>} - Array of cached URLs
 */
export async function cacheMultipleImages(images) {
  const results = await Promise.allSettled(
    images.map(({ url, productId }) => cacheImageToFirebase(url, productId))
  );

  return results.map((result, index) => {
    if (result.status === 'fulfilled') {
      return result.value;
    } else {
      console.error(`Failed to cache image ${index}:`, result.reason);
      // Return original URL on failure
      const original = images[index].url;
      return original.startsWith('http://') 
        ? original.replace('http://', 'https://')
        : original;
    }
  });
}
